import { useState, useEffect, lazy, Suspense } from "react";
import { safeSet, safeGet, safeRemove } from "./utils/safeStorage";
import { syncDisplayMode } from "./utils/displayMode";

// Boot-time cleanup (v16.56). listsScrollPos is written every time the user
// leaves the Lists page so that navigating BACK can restore the position. It
// has no expiry, and on iOS a Safari tab's sessionStorage can survive for
// days - so a leftover value from the previous session made a cold start
// open the Lists page mid-scroll with the header off-screen. Same stale-flag
// class as hasPinEnabled / onboardingComplete (v16.55). Clearing it here,
// at module load before any page mounts, guarantees a fresh boot starts at
// the top while back-navigation restore keeps working within the session.
// (MealPlanning's mp_scrollY needs no equivalent: it carries a timestamp and
// ignores itself after 30 minutes.)
safeRemove("listsScrollPos", true);
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import ErrorBoundary from "./components/shared/ErrorBoundary";
import OfflineSync from "./components/shared/OfflineSync";
import axios from "axios";
import ToastBelowTitle from "./components/shared/ToastBelowTitle";
import { Toaster } from "sonner";
import { 
  ShoppingCart, Utensils, Bell, LogOut, Plus, Check, Trash2, Store, 
  DollarSign, ChevronLeft, Save, X, Minus, Mic, MicOff, Search,
  BarChart3, TrendingUp, BookOpen, ChefHat, Import, Edit3, ChevronRight, Settings, Lock, Key,
  Phone, UserPlus, Users, Moon, Sun, Share2, Download, ScanLine, Copy, ExternalLink, Clock, Receipt
} from "lucide-react";

// Refactored imports
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { BottomNav } from "./components/shared/BottomNav";
import { DeskShell } from "./components/shared/DeskShell";
import { useIsDesk } from "./hooks/useIsDesk";
import { SampleBanner } from "./components/shared/SampleData";
import { isFramedPreview } from "./utils/phonePreview";
import { PinSetupModal } from "./components/shared/PinSetupModal";
import { MemberSetupModal } from "./components/shared/MemberSetupModal";
import XelantiIntro from "./components/XelantiIntro";
// Eagerly imported: auth-flow pages used at boot before login.
// Keeping these eager means the very first paint after a logged-out user
// opens the app doesn't have to wait on a lazy chunk to download.
import { LoginPage } from "./pages/LoginPage";
import { PaywallProvider } from "./contexts/PaywallContext";
import JoinPage from "./pages/JoinPage";
import SharedListPage from "./pages/SharedListPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";
import { ConfirmEmailChangePage } from "./pages/ConfirmEmailChangePage";
import { CancelEmailChangePage } from "./pages/CancelEmailChangePage";

// Lazily imported: heavy protected pages and their dependencies (jsPDF,
// html5-qrcode, recharts, etc.) are pulled in only when the user actually
// navigates to the route. React.lazy needs default exports, so pages that
// use named exports are unwrapped via .then(m => ({ default: m.X })).
const ListDetailPage = lazy(() =>
  import("./pages/ListDetailPage").then(m => ({ default: m.ListDetailPage }))
);
const TemplatesPage = lazy(() =>
  import("./pages/TemplatesPage").then(m => ({ default: m.TemplatesPage }))
);
const MealPlanningPage = lazy(() =>
  import("./pages/MealPlanningPage").then(m => ({ default: m.MealPlanningPage }))
);
const AnalyticsPage = lazy(() =>
  import("./pages/AnalyticsPage").then(m => ({ default: m.AnalyticsPage }))
);
const SettingsPage = lazy(() =>
  import("./pages/SettingsPage").then(m => ({ default: m.SettingsPage }))
);
const CategoriesPage = lazy(() =>
  import("./pages/CategoriesPage").then(m => ({ default: m.CategoriesPage }))
);
const FavouritesPage = lazy(() =>
  import("./pages/FavouritesPage").then(m => ({ default: m.FavouritesPage }))
);
const EmergencyRecoveryPage = lazy(() =>
  import("./pages/EmergencyRecoveryPage").then(m => ({ default: m.EmergencyRecoveryPage }))
);

const AdminPage = lazy(() => import("./pages/AdminPage"));
const PriceComparisonPage = lazy(() =>
  import("./pages/PriceComparisonPage").then(m => ({ default: m.PriceComparisonPage }))
);
const ListsPage = lazy(() =>
  import("./pages/ListsPage").then(m => ({ default: m.ListsPage }))
);
const RemindersPage = lazy(() =>
  import("./pages/RemindersPage").then(m => ({ default: m.RemindersPage }))
);
const RecipesPage = lazy(() =>
  import("./pages/RecipesPage").then(m => ({ default: m.RecipesPage }))
);
const CustomBarcodesPage = lazy(() => import("./pages/CustomBarcodesPage"));
const ManageStoresPage = lazy(() => import("./pages/ManageStoresPage"));
const PrivacyPolicyPage = lazy(() =>
  import("./pages/LegalPages").then(m => ({ default: m.PrivacyPolicyPage }))
);
const TermsPage = lazy(() =>
  import("./pages/LegalPages").then(m => ({ default: m.TermsPage }))
);
const TourPage = lazy(() => import("./pages/TourPage"));

import ScrollMemory from "./components/shared/ScrollMemory";

// Protected Route - uses imported BottomNav and PinSetupModal
const ProtectedRoute = ({ children }) => {
  const { user, loading, showPinSetup, showMemberSetup } = useAuth();
  const location = useLocation();
  // V19.19: a computer-sized window (1024 px+) gets the left menu and top
  // bar instead of the bottom menu. Below that, exactly as before.
  const isDesk = useIsDesk();
  
  if (loading) return <div className="app-container flex items-center justify-center min-h-screen text-gray-400">Loading...</div>;
  // REMEMBER WHERE THEY WERE GOING. (12 Sep 2026)
  //
  // This used to be a bare redirect, so the destination was thrown away. Tap a
  // "Top Up Items" link from a notify message while signed out, sign in, and
  // you land on the lists page having to find the list yourself - which is the
  // entire point of the notify flow, lost at the last step.
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return (
    <>
      {/* PAGE SWIPING REMOVED, 4 October 2026, on Ivan's word.
       *
       *   SwipeablePageWrapper claimed any horizontal move over 10px and slid
       *   the whole app sideways to the next tab. Three things were wrong
       *   with keeping it:
       *
       *     - it fought drag-and-drop. Carrying a meal from Breakfast across
       *       to Dinner is a horizontal move, so the page went with it.
       *     - it wrapped every screen in a translateX transform, which makes
       *       a fixed-position child position against the WRAPPER instead of
       *       the viewport. That is why several modals had to be escaped
       *       through portals.
       *     - the bottom bar already does the job, and does it predictably.
       *
       *   The fork hit the same three and deleted theirs first. The component
       *   file is gone too, not just unused - an unused gesture handler is a
       *   gesture handler somebody re-mounts in a year.
       */}
      {children}
      {isDesk ? <DeskShell /> : <BottomNav />}
      {/* V19.24: shows only while sample data is on. */}
      <SampleBanner />
      {showPinSetup && <PinSetupModal />}
      {showMemberSetup && <MemberSetupModal />}
    </>
  );
};

// Global accessibility wrapper - applies the all-caps and font-size settings
// as CSS state on the document root. Listens for storage / same-tab updates so
// changing them in Settings takes effect everywhere instantly.
//
// Deliberately holds NO state: see utils/displayMode.js. The previous version
// derived the class from React state, so an event carrying an unchanged value
// bailed out of rendering and never re-applied anything - leaving no way back
// if the class went missing.
const GlobalModeWrapper = ({ children }) => {
  useEffect(() => {
    const apply = () => syncDisplayMode(document.documentElement);
    apply();
    window.addEventListener('storage', apply);
    window.addEventListener('mode-changed', apply);
    // Coming back from the background is the other moment the DOM can be out
    // of step with storage - another tab or window may have changed it.
    document.addEventListener('visibilitychange', apply);
    return () => {
      window.removeEventListener('storage', apply);
      window.removeEventListener('mode-changed', apply);
      document.removeEventListener('visibilitychange', apply);
    };
  }, []);

  return children;
};

// Main App
function App() {
  // Brand intro: plays once, on the very first launch of the app on this
  // device. Replayable from Settings -> Help & Support. Skipped on /admin.
  const [showIntro, setShowIntro] = useState(
    () => !safeGet("xelantiIntroSeen") &&
          !window.location.pathname.startsWith("/admin") &&
          !isFramedPreview()   // V19.19: not inside the phone preview frame
  );
  const introDone = () => {
    safeSet("xelantiIntroSeen", "1");
    setShowIntro(false);
  };
  return (
    <AuthProvider>
      {showIntro && <XelantiIntro onDone={introDone} />}
      {/* Sends changes kept on this phone while offline (V19.14). */}
      <OfflineSync />
      <GlobalModeWrapper>
      {/* Full-screen overlays in the app sit at z-index 10000-10050, so the
          toaster must sit above them - otherwise an error raised from inside a
          modal (a failed payer assignment, say) is rendered underneath it and
          the action just looks like it silently did nothing (v15.07). */}
      {/* TOP, not bottom (V18.98, live test): at the bottom a toast sat on
          the very buttons and inputs it was talking about - "Give the
          reminder a name" covered the reminder's own Save, "Store added"
          covered the Add Store field. Below the phone's notch / status bar.
          Lowered under the screen title row (V19.03, full test item 29: at
          12 px the pill still sat on the titles of Reminders, Manage Stores,
          Settings and the list screens, whose header row is about 72 px). */}
      {/* V19.04 (live test 19.03 item 12, still on titles at 76 px): the
          place is measured from the title on screen - see ToastBelowTitle. */}
      <ToastBelowTitle />
      <Toaster 
        position="top-center" 
        offset={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
        mobileOffset={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
        duration={2000}
        style={{ zIndex: 100000 }}
        toastOptions={{ 
          style: { background: '#000', color: '#fff', borderRadius: '100px' }
        }} 
      />
      <BrowserRouter>
        <ScrollMemory />
        <Suspense fallback={
          <div className="app-container flex items-center justify-center min-h-screen text-gray-400">
            Loading...
          </div>
        }>
          <PaywallProvider>
          <ErrorBoundary>
          <Routes>
          {/* The feature walkthrough was removed (V19.14, Ivan 17 Oct). An old
              link or bookmark to it goes home. */}
          <Route path="/onboarding" element={<Navigate to="/" replace />} />
          <Route path="/login" element={<LoginPage />} />
          {/* PUBLIC, and it has to be. (18 Sep 2026)
              A scanned family QR lands here, and the person holding the phone
              is by definition NOT signed in yet - that is the whole point.
              Behind ProtectedRoute it would bounce to /login and lose the
              code from the URL.

              JoinPage was written, imported at the top of this file, and
              never rendered in a Route. It shipped in the bundle and was
              reachable by no URL. In-app scanning worked the whole time,
              because QrLoginScanner parses the code out of the decoded text
              and never navigates - so every test of the feature used the one
              path that does not touch this route.

              The Tradie's Bay chat found the identical thing in their fork,
              same file, same missing line. */}
          <Route path="/join" element={<JoinPage />} />
          {/* PUBLIC, like /join and for the same reason: the person opening a
              shared list does not have the app and is not signed in. Behind
              ProtectedRoute it would bounce to the sign-in screen, which is
              exactly what happened for months when this route did not exist
              at all. (18 Sep 2026) */}
          <Route path="/shared/:shareId" element={<SharedListPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/emergency-recovery" element={<EmergencyRecoveryPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/confirm-email-change" element={<ConfirmEmailChangePage />} />
          <Route path="/cancel-email-change" element={<CancelEmailChangePage />} />
          <Route path="/tour" element={<TourPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/" element={<ProtectedRoute><ListsPage /></ProtectedRoute>} />
          <Route path="/list/:id" element={<ProtectedRoute><KeyedListDetailPage /></ProtectedRoute>} />
          <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
          <Route path="/meal-planning" element={<ProtectedRoute><MealPlanningPage /></ProtectedRoute>} />
          <Route path="/reminders" element={<ProtectedRoute><RemindersPage /></ProtectedRoute>} />
          <Route path="/recipes" element={<ProtectedRoute><RecipesPage /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
          <Route path="/price-comparison" element={<ProtectedRoute><PriceComparisonPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/settings/categories" element={<ProtectedRoute><CategoriesPage /></ProtectedRoute>} />
          <Route path="/settings/favourites" element={<ProtectedRoute><FavouritesPage /></ProtectedRoute>} />
          <Route path="/custom-barcodes" element={<ProtectedRoute><CustomBarcodesPage /></ProtectedRoute>} />
          <Route path="/stores" element={<ProtectedRoute><ManageStoresPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
          </ErrorBoundary>
          </PaywallProvider>
        </Suspense>
      </BrowserRouter>
      </GlobalModeWrapper>
    </AuthProvider>
  );
}

// V19.08: one page per list. Opening another list from a list (the
// unbought items moved to a new list) starts that page fresh instead of
// carrying the old list's state across.
function KeyedListDetailPage() {
  const { id } = useParams();
  return <ListDetailPage key={id} />;
}

export default App;
